// ============================================================
// Craft a Brew — Recipe Builder Mash Water Add-on v2.7
// Date: April 14, 2026
//
// Load AFTER recipe-builder.js, recipe-builder-enhancements.js,
// and recipe-pdf-export.js. Order otherwise doesn't matter —
// this file is self-contained and patches existing globals with
// wrappers rather than editing any of the other files.
//
// TO TOGGLE OFF: comment out or remove the <script> tag that
// loads this file. Zero other changes needed.
//
// ============================================================
// WHAT'S NEW IN v2.0 (vs v1.0)
// ------------------------------------------------------------
//
// 1. FERMENTABILITY MODEL — the big one.
//    The mash temp dropdown now actually affects the recipe's
//    FG, and therefore ABV, BU:GU, BJCP style match, tasting
//    notes, and fermentation timeline. Previously it was
//    informational only, which Kyle (correctly) called out as
//    wrong — for a recipe builder to be useful to all-grain
//    brewers, mash temp HAS to work in tandem with yeast
//    attenuation to give a realistic FG estimate.
//
//    THE SCIENCE: Mash temperature controls which enzyme
//    dominates starch conversion:
//      - 148-150°F → beta amylase dominant → more simple
//        fermentable sugars (maltose) → more fermentable wort
//      - 152°F → balance point (most published yeast
//        attenuation figures are measured on 150-152°F wort)
//      - 154-158°F → alpha amylase dominant → more dextrins
//        and complex sugars → less fermentable wort
//
//    Yeast attenuation specs (e.g., Safale US-05 at 81%) are
//    measured on a standard 150-152°F wort. When you mash
//    hotter or cooler, the wort's actual fermentability
//    shifts, and the effective attenuation shifts with it —
//    even though the yeast itself hasn't changed.
//
//    THE MODEL: ~1.2 percentage points of attenuation shift
//    per °F of deviation from 152°F baseline. Source: Palmer's
//    How to Brew, backed by Brewer's Friend's calculator. This
//    is a middle-ground estimate — some sources cite 1.0pp/°F,
//    others 1.5pp/°F. 1.2 hits the middle and produces
//    realistic results across the full 148-158°F range.
//
//    EXAMPLES with Safale US-05 (81% published):
//      - 148°F → +4.8pp → 85.8% effective (very dry)
//      - 150°F → +2.4pp → 83.4%
//      - 152°F → baseline → 81% (as published)
//      - 154°F → -2.4pp → 78.6%
//      - 156°F → -4.8pp → 76.2%
//      - 158°F → -7.2pp → 73.8% (fuller, sweeter)
//
//    Effective attenuation is bounded 40%-95% so extreme
//    yeast/temp combinations stay physically plausible.
//
//    CASCADE: I verified by reading recipe-builder.js that
//    recipe.yeast.attenuation is only read in ONE place —
//    inside recalculate() for the FG computation. So by
//    temporarily mutating recipe.yeast.attenuation before the
//    original recalculate runs, and restoring it after, the
//    adjusted FG cascades correctly to:
//      - ABV via (og-fg) * 131.25
//      - BJCP style match FG range check
//      - Tasting notes ("medium-bodied" etc.)
//      - Fermentation chart curves
//      - "Est Days" via calcFermDays (reads OG, unaffected)
//    No other function reads attenuation, so there are no
//    side effects.
//
// 2. STYLING — matches the rest of the recipe builder.
//    - Body background changed from pure black to the same
//      cream/wash tone used by the yeast info grid. Still
//      visually distinct from the white cards around it so
//      the section reads as "optional intermediate tool" but
//      no longer feels like a black box.
//    - Title font: Kreon uppercase with 0.18em letter spacing,
//      matching FERMENTABLES/HOPS/YEAST.
//    - Icon: inline SVG of lucide "droplets" in marigold,
//      matching the icon pattern used by the other card
//      headers. No dependency on lucide being loaded —
//      the SVG renders immediately even before lucide runs.
//    - Inputs: white with hairline border, matching the
//      Batch Basics styling.
//    - Result cards: serif value + slab label, matching the
//      Recipe Stats grid on the right sidebar.
//
// 3. LIVE ATTENUATION NOTE — when yeast is selected, the
//    expanded panel now shows a marigold-left-rule info block
//    explaining the current adjustment, with both the
//    attenuation delta and the FG delta. Example:
//      "Warmer mash (154°F) → less fermentable wort.
//       Safale US-05's effective attenuation shifts 81% → 78.6%.
//       FG: 1.011 → 1.013 (fuller body, lower ABV)."
//    This gives the brewer immediate feedback on exactly how
//    their mash temp choice is affecting the recipe.
//
// 4. FORCED INITIAL RECALC — after the add-on finishes
//    wrapping recalculate, it calls recalculate() once so
//    any state already restored by recipe-builder-enhancements.js
//    (the welcome-back recipe restore) gets re-computed with
//    the mash temp adjustment applied. Without this, a freshly
//    loaded saved recipe would show the wrong FG until the
//    user made any change.
//
// ============================================================

(function () {
  'use strict';

  var STORAGE_KEY = 'cab_mash_water_v1';

  // Baseline mash temp where published yeast attenuation applies
  var MASH_TEMP_BASELINE = 152;
  // Percentage-point shift in attenuation per °F of mash temp
  // deviation from the baseline. See v2.0 notes above for
  // sourcing — this is the middle of the range cited by Palmer
  // and Brewer's Friend.
  var ATTENUATION_PP_PER_DEG = 1.2;
  // Safety bounds for the adjusted attenuation
  var MIN_EFFECTIVE_ATTENUATION = 40;
  var MAX_EFFECTIVE_ATTENUATION = 95;

  // Fermentables we exclude from the grain weight total for
  // strike/sparge calcs — matches the spec exactly.
  var NON_GRAIN_TOKENS = [
    'DME', 'Sugar', 'Dextrose', 'Lactose', 'Maltodextrin',
    'Candi Sugar', 'Demerara', 'Brewers Crystals'
  ];

  // Inline lucide "droplets" icon SVG. Embedded so we don't
  // depend on lucide being loaded (the enhancements file
  // loads it async, timing is not guaranteed).
  var DROPLETS_SVG =
    '<svg class="mash-water-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M7 16.3c2.2 0 4-1.83 4-4.05 0-1.16-.57-2.26-1.71-3.19S7.29 6.75 7 5.3c-.29 1.45-1.14 2.84-2.29 3.76S3 11.1 3 12.25c0 2.22 1.8 4.05 4 4.05z"/>' +
    '<path d="M12.56 6.6A10.97 10.97 0 0 0 14 3.02c.5 2.5 2 4.9 4 6.5s3 3.5 3 5.5a6.98 6.98 0 0 1-11.91 4.97"/>' +
    '</svg>';

  // ============================================================
  // Bootstrap — wait for the calculator's globals and DOM
  // ============================================================
  function ready(fn) {
    if (
      window.PRODUCTS &&
      typeof window.recalculate === 'function' &&
      typeof window.updateMethodDisplay === 'function' &&
      document.getElementById('brewMethod') &&
      document.getElementById('efficiencyGroup') &&
      document.querySelector('.cab-rb .settings-bar')
    ) {
      fn();
    } else {
      setTimeout(function () { ready(fn); }, 100);
    }
  }
  ready(init);

  function init() {
    try {
      injectStyles();
      injectMashTempSetting();
      injectMashWaterPanel();
      wrapUpdateMethodDisplay();
      wrapRecalculate();
      restoreState();
      applyMethodVisibility();
      calculate();
      hookPdfExport();
      hookPrintExport();

      // Force one recalc so the mash temp adjustment applies
      // to any state already restored by enhancements.js before
      // our wrapper was in place. See v2.0 note #4.
      if (typeof window.recalculate === 'function') {
        try { window.recalculate(); } catch (e) {}
      }

      console.log('[CAB] Mash water add-on v2.7 loaded.');
    } catch (e) {
      console.error('[CAB] Mash water add-on failed to initialize:', e);
    }
  }

  // ============================================================
  // Styles
  // ============================================================
  function injectStyles() {
    var css = [
      /* Outer panel — matches .card visual weight but visibly
         compact so it reads as an optional tool */
      '.cab-rb .mash-water-panel {',
      '  background: #FFFFFF !important;',
      '  border: 1px solid var(--cab-hairline, #E5E5E5) !important;',
      '  border-radius: 8px !important;',
      '  box-shadow: 0 4px 16px rgba(45,42,38,0.08), 0 1px 4px rgba(45,42,38,0.05) !important;',
      '  margin-bottom: 36px !important;',
      '  overflow: hidden !important;',
      '}',

      /* Dark header bar matching .card-header */
      '.cab-rb .mash-water-header {',
      '  background: var(--cab-ink, #3D3A35) !important;',
      '  padding: 18px 24px !important;',
      '  display: flex !important;',
      '  align-items: center !important;',
      '  gap: 16px !important;',
      '  cursor: pointer !important;',
      '  user-select: none !important;',
      '  transition: background 0.15s ease !important;',
      '}',
      '.cab-rb .mash-water-header:hover { background: #4a4640 !important; }',

      /* Icon — marigold lucide droplets, 22px to match .card-title .cab-icon */
      '.cab-rb .mash-water-icon-svg {',
      '  width: 22px !important;',
      '  height: 22px !important;',
      '  color: var(--cab-marigold, #F5A623) !important;',
      '  stroke: var(--cab-marigold, #F5A623) !important;',
      '  flex-shrink: 0 !important;',
      '  display: block !important;',
      '}',

      '.cab-rb .mash-water-header-text { flex: 1 !important; min-width: 0 !important; }',

      /* Title — Kreon uppercase matching .card-title */
      '.cab-rb .mash-water-title {',
      '  font-family: var(--cab-slab, "Kreon", Georgia, serif) !important;',
      '  font-size: 16px !important;',
      '  font-weight: 700 !important;',
      '  letter-spacing: 0.18em !important;',
      '  text-transform: uppercase !important;',
      '  color: #FFFFFF !important;',
      '  line-height: 1.2 !important;',
      '}',

      /* Tagline — DM Sans, subtle white */
      '.cab-rb .mash-water-tagline {',
      '  font-family: var(--cab-sans, "DM Sans", sans-serif) !important;',
      '  font-size: 12px !important;',
      '  font-weight: 400 !important;',
      '  color: rgba(255,255,255,0.6) !important;',
      '  margin-top: 4px !important;',
      '  letter-spacing: 0.02em !important;',
      '}',

      /* Chevron */
      '.cab-rb .mash-water-chevron {',
      '  color: var(--cab-marigold, #F5A623) !important;',
      '  font-size: 20px !important;',
      '  line-height: 1 !important;',
      '  flex-shrink: 0 !important;',
      '  transition: transform 0.2s ease !important;',
      '}',
      '.cab-rb .mash-water-panel.expanded .mash-water-chevron {',
      '  transform: rotate(180deg) !important;',
      '}',

      /* Body — cream/wash background matching the yeast info grid */
      '.cab-rb .mash-water-body {',
      '  display: none !important;',
      '  padding: 24px !important;',
      '  background: var(--cab-wash, #F4F4F8) !important;',
      '}',
      '.cab-rb .mash-water-panel.expanded .mash-water-body {',
      '  display: block !important;',
      '}',

      /* Inputs row */
      '.cab-rb .mash-water-inputs {',
      '  display: flex !important;',
      '  gap: 24px !important;',
      '  flex-wrap: wrap !important;',
      '  margin-bottom: 20px !important;',
      '}',
      '.cab-rb .mash-water-inputs .setting-group {',
      '  flex: 1 !important;',
      '  min-width: 220px !important;',
      '}',
      '.cab-rb .mash-water-inputs .setting-label {',
      '  font-family: var(--cab-slab, "Kreon", serif) !important;',
      '  font-size: 12px !important;',
      '  font-weight: 600 !important;',
      '  letter-spacing: 0.18em !important;',
      '  color: var(--cab-ink, #3D3A35) !important;',
      '  text-transform: uppercase !important;',
      '  margin-bottom: 12px !important;',
      '  display: block !important;',
      '}',
      '.cab-rb .mash-water-inputs input,',
      '.cab-rb .mash-water-inputs select {',
      '  width: 100% !important;',
      '  background: #FFFFFF !important;',
      '  background-color: #FFFFFF !important;',
      '  color: var(--cab-body, #1A1A1A) !important;',
      '  border: 1px solid var(--cab-hairline, #E5E5E5) !important;',
      '  border-radius: 8px !important;',
      '  padding: 12px 16px !important;',
      '  font-family: var(--cab-sans, "DM Sans", sans-serif) !important;',
      '  font-size: 15px !important;',
      '}',
      '.cab-rb .mash-water-inputs input:focus,',
      '.cab-rb .mash-water-inputs select:focus {',
      '  outline: none !important;',
      '  border-color: var(--cab-marigold, #F5A623) !important;',
      '  box-shadow: 0 0 0 3px rgba(245,166,35,0.18) !important;',
      '}',

      /* Results grid — matches .stats-grid on the sidebar */
      '.cab-rb .mash-water-results {',
      '  display: grid !important;',
      '  grid-template-columns: repeat(4, 1fr) !important;',
      '  gap: 1px !important;',
      '  background: var(--cab-hairline, #E5E5E5) !important;',
      '  border: 1px solid var(--cab-hairline, #E5E5E5) !important;',
      '  border-radius: 8px !important;',
      '  overflow: hidden !important;',
      '  margin-bottom: 16px !important;',
      '}',
      '@media (max-width: 720px) {',
      '  .cab-rb .mash-water-results { grid-template-columns: repeat(2, 1fr) !important; }',
      '}',
      '.cab-rb .mash-water-result {',
      '  background: #FFFFFF !important;',
      '  padding: 20px 12px !important;',
      '  text-align: center !important;',
      '}',
      '.cab-rb .mash-water-result-value {',
      '  font-family: var(--cab-serif, "Kreon", Georgia, serif) !important;',
      '  font-size: 1.6rem !important;',
      '  font-weight: 500 !important;',
      '  color: var(--cab-ink, #3D3A35) !important;',
      '  line-height: 1.1 !important;',
      '}',
      '.cab-rb .mash-water-result-label {',
      '  font-family: var(--cab-slab, "Kreon", serif) !important;',
      '  font-size: 11px !important;',
      '  font-weight: 600 !important;',
      '  letter-spacing: 0.14em !important;',
      '  color: var(--cab-marigold-dark, #E89405) !important;',
      '  text-transform: uppercase !important;',
      '  margin-top: 8px !important;',
      '}',

      /* Live attenuation note — marigold left rule, white inside */
      '.cab-rb .mash-water-atten-note {',
      '  background: #FFFFFF !important;',
      '  border: 1px solid var(--cab-hairline, #E5E5E5) !important;',
      '  border-left: 3px solid var(--cab-marigold, #F5A623) !important;',
      '  border-radius: 0 6px 6px 0 !important;',
      '  padding: 14px 18px !important;',
      '  margin-bottom: 14px !important;',
      '  font-family: var(--cab-sans, "DM Sans", sans-serif) !important;',
      '  font-size: 13px !important;',
      '  line-height: 1.55 !important;',
      '  color: var(--cab-body, #1A1A1A) !important;',
      '}',
      '.cab-rb .mash-water-atten-note strong {',
      '  color: var(--cab-ink, #3D3A35) !important;',
      '  font-weight: 600 !important;',
      '}',
      '.cab-rb .mash-water-atten-note .atten-emphasis {',
      '  color: var(--cab-marigold-dark, #E89405) !important;',
      '  font-weight: 700 !important;',
      '}',

      /* Footer note */
      '.cab-rb .mash-water-note {',
      '  font-family: var(--cab-sans, "DM Sans", sans-serif) !important;',
      '  font-size: 12px !important;',
      '  color: var(--cab-muted, #6B6B6B) !important;',
      '  font-style: italic !important;',
      '  line-height: 1.5 !important;',
      '}'
    ].join('\n');

    var style = document.createElement('style');
    style.id = 'cab-mash-water-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // ============================================================
  // Feature 1 — Mash Temp dropdown in Batch Basics
  // ============================================================
  function injectMashTempSetting() {
    var effGroup = document.getElementById('efficiencyGroup');
    if (!effGroup || !effGroup.parentNode) return;

    var group = document.createElement('div');
    group.className = 'setting-group';
    group.id = 'mashTempGroup';
    group.innerHTML =
      '<div class="setting-label">Mash Temp</div>' +
      '<select id="mashTemp" class="setting-select">' +
      '<option value="148">148\u00B0F \u2014 Dry / Light Body</option>' +
      '<option value="149">149\u00B0F \u2014 Dry / Light Body</option>' +
      '<option value="150">150\u00B0F \u2014 Medium-Dry Body</option>' +
      '<option value="151">151\u00B0F \u2014 Medium Body</option>' +
      '<option value="152" selected>152\u00B0F \u2014 Medium Body (Most Common)</option>' +
      '<option value="153">153\u00B0F \u2014 Medium Body</option>' +
      '<option value="154">154\u00B0F \u2014 Medium-Full Body</option>' +
      '<option value="155">155\u00B0F \u2014 Full Body</option>' +
      '<option value="156">156\u00B0F \u2014 Full / Sweet Body</option>' +
      '<option value="158">158\u00B0F \u2014 Very Full / Sweet Body</option>' +
      '</select>';

    effGroup.parentNode.insertBefore(group, effGroup.nextSibling);

    document.getElementById('mashTemp').addEventListener('change', function () {
      // A mash temp change cascades through recalculate() via
      // the wrapper, which handles the attenuation adjustment,
      // FG/ABV update, and then calls calculate() at the end
      // for the mash water strike/sparge values + atten note.
      if (typeof window.recalculate === 'function') window.recalculate();
      saveState();
    });
  }

  // ============================================================
  // Feature 2 — Mash Water Calculator collapsible panel
  // ============================================================
  function injectMashWaterPanel() {
    var settingsBar = document.querySelector('.cab-rb .settings-bar');
    if (!settingsBar || !settingsBar.parentNode) return;

    var panel = document.createElement('div');
    panel.className = 'mash-water-panel';
    panel.id = 'mashWaterPanel';
    panel.innerHTML =
      '<div class="mash-water-header" id="mashWaterHeader">' +
        DROPLETS_SVG +
        '<div class="mash-water-header-text">' +
          '<div class="mash-water-title">Mash Water Calculator</div>' +
          '<div class="mash-water-tagline">Calculate your strike water volume and temperature</div>' +
        '</div>' +
        '<div class="mash-water-chevron">\u25BE</div>' +
      '</div>' +
      '<div class="mash-water-body">' +
        '<div class="mash-water-inputs">' +
          '<div class="setting-group">' +
            '<label class="setting-label" for="grainTemp">Grain Temp (\u00B0F)</label>' +
            '<input type="number" id="grainTemp" value="68" min="32" max="120" step="1">' +
          '</div>' +
          '<div class="setting-group">' +
            '<label class="setting-label" for="mashRatio">Water-to-Grain Ratio</label>' +
            '<select id="mashRatio">' +
              '<option value="1.25">1.25 qt/lb \u2014 Thick Mash</option>' +
              '<option value="1.33">1.33 qt/lb \u2014 Thick Mash</option>' +
              '<option value="1.5" selected>1.5 qt/lb \u2014 Standard</option>' +
              '<option value="1.75">1.75 qt/lb \u2014 Standard</option>' +
              '<option value="2.0">2.0 qt/lb \u2014 Thin Mash / BIAB</option>' +
              '<option value="2.5">2.5 qt/lb \u2014 BIAB</option>' +
              '<option value="3.0">3.0 qt/lb \u2014 Full Volume BIAB</option>' +
            '</select>' +
          '</div>' +
        '</div>' +
        '<div class="mash-water-results">' +
          '<div class="mash-water-result"><div class="mash-water-result-value" id="strikeVolOut">\u2014</div><div class="mash-water-result-label">Strike Water</div></div>' +
          '<div class="mash-water-result"><div class="mash-water-result-value" id="strikeTempOut">\u2014</div><div class="mash-water-result-label">Strike Temp</div></div>' +
          '<div class="mash-water-result"><div class="mash-water-result-value" id="spargeVolOut">\u2014</div><div class="mash-water-result-label">Sparge Water</div></div>' +
          '<div class="mash-water-result"><div class="mash-water-result-value" id="totalWaterOut">\u2014</div><div class="mash-water-result-label">Total Water</div></div>' +
        '</div>' +
        '<div class="mash-water-atten-note" id="mashAttenNote" style="display:none;"></div>' +
        '<div class="mash-water-note">\uD83D\uDCA1 Assumes a single-infusion mash. Mash efficiency (in Batch Basics) is separate \u2014 it affects OG, not this water math.</div>' +
      '</div>';

    settingsBar.parentNode.insertBefore(panel, settingsBar.nextSibling);

    document.getElementById('mashWaterHeader').addEventListener('click', function () {
      document.getElementById('mashWaterPanel').classList.toggle('expanded');
      saveState();
    });
    document.getElementById('grainTemp').addEventListener('input', function () {
      calculate();
      saveState();
    });
    document.getElementById('mashRatio').addEventListener('change', function () {
      calculate();
      saveState();
    });
  }

  // ============================================================
  // Fermentability model — the heart of v2.0
  // ============================================================
  function getEffectiveAttenuation(publishedAtt, mashTemp) {
    // publishedAtt is a percentage number, e.g. 81 for 81%
    // mashTemp is °F
    // Returns the mash-temp-adjusted effective attenuation,
    // bounded 40%-95% so extreme combinations stay physical.
    var delta = MASH_TEMP_BASELINE - mashTemp; // positive when cooler
    var shift = delta * ATTENUATION_PP_PER_DEG;
    var effective = publishedAtt + shift;
    if (effective < MIN_EFFECTIVE_ATTENUATION) return MIN_EFFECTIVE_ATTENUATION;
    if (effective > MAX_EFFECTIVE_ATTENUATION) return MAX_EFFECTIVE_ATTENUATION;
    return effective;
  }

  // Live info note rendered under the results grid explaining
  // exactly how mash temp is shifting attenuation and FG for
  // the currently-selected yeast.
  function updateAttenuationNote(mashTemp) {
    var noteEl = document.getElementById('mashAttenNote');
    if (!noteEl) return;

    var methodEl = document.getElementById('brewMethod');
    if (!methodEl || methodEl.value !== 'allgrain') {
      noteEl.style.display = 'none';
      return;
    }

    if (!window.recipe || !window.recipe.yeast) {
      noteEl.style.display = 'none';
      return;
    }

    var publishedAtt = window.recipe.yeast.attenuation;
    if (publishedAtt == null) {
      noteEl.style.display = 'none';
      return;
    }

    var effectiveAtt = getEffectiveAttenuation(publishedAtt, mashTemp);

    var ogEl = document.getElementById('ogValue');
    var og = ogEl ? parseFloat(ogEl.textContent) : NaN;
    if (!og || isNaN(og) || og < 1.001) {
      // No real OG yet — user hasn't added fermentables.
      noteEl.style.display = 'none';
      return;
    }

    var baselineFg = og - (og - 1) * (publishedAtt / 100);
    var currentFg = og - (og - 1) * (effectiveAtt / 100);

    var yeastName = (window.recipe.yeast.name || 'your yeast').replace(/\s*\(.*\)\s*$/, '');
    var delta = mashTemp - MASH_TEMP_BASELINE;

    var msg;
    if (Math.abs(delta) < 0.5) {
      msg =
        '<strong>Baseline mash (152\u00B0F).</strong> ' + escapeHtml(yeastName) +
        ' ferments at its published <span class="atten-emphasis">' + publishedAtt.toFixed(0) + '%</span> attenuation. ' +
        'Estimated FG: <span class="atten-emphasis">' + currentFg.toFixed(3) + '</span>.';
    } else if (delta < 0) {
      msg =
        '<strong>Cooler mash (' + mashTemp + '\u00B0F) \u2192 more fermentable wort.</strong> ' +
        escapeHtml(yeastName) + '\'s effective attenuation shifts ' +
        publishedAtt.toFixed(0) + '% \u2192 <span class="atten-emphasis">' + effectiveAtt.toFixed(1) + '%</span>. ' +
        'FG: ' + baselineFg.toFixed(3) + ' \u2192 <span class="atten-emphasis">' + currentFg.toFixed(3) + '</span> ' +
        '(drier finish, higher ABV).';
    } else {
      msg =
        '<strong>Warmer mash (' + mashTemp + '\u00B0F) \u2192 less fermentable wort.</strong> ' +
        escapeHtml(yeastName) + '\'s effective attenuation shifts ' +
        publishedAtt.toFixed(0) + '% \u2192 <span class="atten-emphasis">' + effectiveAtt.toFixed(1) + '%</span>. ' +
        'FG: ' + baselineFg.toFixed(3) + ' \u2192 <span class="atten-emphasis">' + currentFg.toFixed(3) + '</span> ' +
        '(fuller body, lower ABV).';
    }

    noteEl.innerHTML = msg;
    noteEl.style.display = 'block';
  }

  // ============================================================
  // Mash water volume/temp calculations
  // ============================================================
  function isMashableGrain(name) {
    if (!name) return false;
    for (var i = 0; i < NON_GRAIN_TOKENS.length; i++) {
      if (name.indexOf(NON_GRAIN_TOKENS[i]) >= 0) return false;
    }
    return true;
  }

  function getTotalGrainWeightLbs() {
    var total = 0;
    document.querySelectorAll('#fermentablesBody tr').forEach(function (row) {
      var sel = row.querySelector('.ingredient-select');
      if (!sel || !sel.value) return;
      var opt = sel.options[sel.selectedIndex];
      if (!opt) return;
      if (!isMashableGrain(opt.text || '')) return;

      var amtInput = row.querySelector('.amount-input');
      var unitSel = row.querySelector('.unit-select');
      var raw = parseFloat(amtInput ? amtInput.value : 0) || 0;
      var lbs = unitSel && unitSel.value === 'oz' ? raw / 16 : raw;
      total += lbs;
    });
    return total;
  }

  function calculate() {
    var methodEl = document.getElementById('brewMethod');
    if (!methodEl || methodEl.value !== 'allgrain') return;

    var mashTempEl = document.getElementById('mashTemp');
    var grainTempEl = document.getElementById('grainTemp');
    var ratioEl = document.getElementById('mashRatio');
    var boilSizeEl = document.getElementById('boilSize');
    if (!mashTempEl || !grainTempEl || !ratioEl || !boilSizeEl) return;

    var mashTemp = parseFloat(mashTempEl.value) || MASH_TEMP_BASELINE;
    var grainTemp = parseFloat(grainTempEl.value) || 68;
    var ratio = parseFloat(ratioEl.value) || 1.5;
    var boilSize = parseFloat(boilSizeEl.value) || 6;
    var totalGrain = getTotalGrainWeightLbs();

    // Always refresh the attenuation note (even if no grain yet —
    // the note handles its own visibility based on whether a
    // yeast is selected and OG is real).
    updateAttenuationNote(mashTemp);

    if (totalGrain <= 0) {
      setText('strikeVolOut', '\u2014');
      setText('strikeTempOut', '\u2014');
      setText('spargeVolOut', '\u2014');
      setText('totalWaterOut', '\u2014');
      window._cabMashWater = null;
      return;
    }

    // Strike water volume: (grain lb × ratio qt/lb) ÷ 4 qt/gal
    var strikeVol = (totalGrain * ratio) / 4;

    // Standard single-infusion strike temp equation
    var strikeTemp = (0.2 / ratio) * (mashTemp - grainTemp) + mashTemp;

    // Total water needed for boil size accounting for grain
    // absorption (0.125 gal/lb absorbed by the grain bed)
    var totalNeeded = boilSize + (totalGrain * 0.125);

    var spargeVol = totalNeeded - strikeVol;
    var spargeIsNone = spargeVol <= 0;
    var totalWater = spargeIsNone ? strikeVol : strikeVol + spargeVol;

    setText('strikeVolOut', strikeVol.toFixed(1) + ' gal');
    setText('strikeTempOut', strikeTemp.toFixed(1) + '\u00B0F');
    setText('spargeVolOut', spargeIsNone ? 'None (Full Vol)' : spargeVol.toFixed(1) + ' gal');
    setText('totalWaterOut', totalWater.toFixed(1) + ' gal');

    // Stash for the PDF + print exporters to read
    window._cabMashWater = {
      mashTemp: mashTemp,
      mashTempLabel: mashTempEl.options[mashTempEl.selectedIndex].text,
      grainTemp: grainTemp,
      ratio: ratio,
      totalGrain: totalGrain,
      strikeVol: strikeVol,
      strikeTemp: strikeTemp,
      spargeVol: spargeIsNone ? null : spargeVol,
      totalWater: totalWater,
      spargeIsNone: spargeIsNone
    };
  }

  // ============================================================
  // Method visibility — show/hide both new UI pieces based on
  // the brew method dropdown. Called from the wrapper.
  // ============================================================
  function applyMethodVisibility() {
    var methodEl = document.getElementById('brewMethod');
    var isAllGrain = methodEl && methodEl.value === 'allgrain';
    var tempGroup = document.getElementById('mashTempGroup');
    var panel = document.getElementById('mashWaterPanel');
    if (tempGroup) tempGroup.style.display = isAllGrain ? 'block' : 'none';
    if (panel) panel.style.display = isAllGrain ? 'block' : 'none';
  }

  // ============================================================
  // Wrappers around the calculator's globals
  // ============================================================
  function wrapUpdateMethodDisplay() {
    var orig = window.updateMethodDisplay;
    if (typeof orig !== 'function') return;
    window.updateMethodDisplay = function () {
      var result = orig.apply(this, arguments);
      applyMethodVisibility();
      calculate();
      return result;
    };
  }

  function wrapRecalculate() {
    var orig = window.recalculate;
    if (typeof orig !== 'function') return;

    window.recalculate = function () {
      // =========================================================
      // FERMENTABILITY INTERCEPT
      // ---------------------------------------------------------
      // Before the original recalculate runs, temporarily mutate
      // recipe.yeast.attenuation so the FG calculation inside
      // recalculate uses the mash-temp-adjusted value. Restore
      // in a finally block so a thrown error doesn't leave the
      // recipe in a corrupt state.
      //
      // recipe.yeast is a REFERENCE to an object in PRODUCTS —
      // mutating it permanently would be bad, hence the stash
      // and restore. This is the cleanest way to layer a FG
      // adjustment on top of recipe-builder.js without editing it.
      // =========================================================
      var didAdjust = false;
      var originalAtt = null;
      var targetYeast = null;

      try {
        var methodEl = document.getElementById('brewMethod');
        var mashTempEl = document.getElementById('mashTemp');
        if (
          methodEl &&
          methodEl.value === 'allgrain' &&
          mashTempEl &&
          window.recipe &&
          window.recipe.yeast &&
          typeof window.recipe.yeast.attenuation === 'number'
        ) {
          var mashTemp = parseFloat(mashTempEl.value);
          if (!isNaN(mashTemp)) {
            targetYeast = window.recipe.yeast;
            originalAtt = targetYeast.attenuation;
            var effective = getEffectiveAttenuation(originalAtt, mashTemp);
            targetYeast.attenuation = effective;
            didAdjust = true;
          }
        }
      } catch (e) {
        // If anything goes wrong setting up the adjustment,
        // skip it and let orig run with published attenuation.
        didAdjust = false;
      }

      var result;
      try {
        result = orig.apply(this, arguments);
      } finally {
        if (didAdjust && targetYeast && originalAtt != null) {
          try { targetYeast.attenuation = originalAtt; } catch (e) {}
        }
      }

      // Now update the mash water results + live attenuation
      // note. This runs with published attenuation restored, so
      // the note's "baseline FG" math uses the right numbers.
      try { calculate(); } catch (e) {}

      return result;
    };
  }

  // ============================================================
  // localStorage persistence
  // ============================================================
  function saveState() {
    try {
      var panel = document.getElementById('mashWaterPanel');
      var state = {
        mashTemp: getVal('mashTemp'),
        grainTemp: getVal('grainTemp'),
        mashRatio: getVal('mashRatio'),
        expanded: panel ? panel.classList.contains('expanded') : false,
        savedAt: Date.now()
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) { /* storage unavailable, silent fail */ }
  }

  function restoreState() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      var s = JSON.parse(raw);
      if (s.mashTemp) setVal('mashTemp', s.mashTemp);
      if (s.grainTemp) setVal('grainTemp', s.grainTemp);
      if (s.mashRatio) setVal('mashRatio', s.mashRatio);
      if (s.expanded) {
        var panel = document.getElementById('mashWaterPanel');
        if (panel) panel.classList.add('expanded');
      }
    } catch (e) { /* corrupt or unavailable, silent fail */ }
  }

  // ============================================================
  // PDF export hook — v2.4: polling + verbose diagnostic logging
  // --------------------------------------------------------------
  // v2.3 polling SHOULD work based on the analysis (100ms poll,
  // jsPDF load + generatePDF drawing takes ~500-1000ms so plenty
  // of window). But Kyle's test shows Print works while Save is
  // still missing the mash water page. Something unexpected is
  // happening. This version logs every step so we can see exactly
  // where the chain breaks.
  // ============================================================
  function hookPdfExport() {
    console.log('[CAB MW] hookPdfExport: starting');
    // Already loaded? Patch immediately.
    if (window.jspdf && window.jspdf.jsPDF) {
      console.log('[CAB MW] hookPdfExport: jsPDF already on window at init, patching now');
      patchJsPDFProto(window.jspdf.jsPDF);
      return;
    }
    // Otherwise poll at 100ms until jsPDF loads, then patch.
    console.log('[CAB MW] hookPdfExport: jsPDF not yet loaded, starting poll');
    var attempts = 0;
    function tryHook() {
      attempts++;
      var jsPDF = window.jspdf && window.jspdf.jsPDF;
      if (jsPDF) {
        console.log('[CAB MW] hookPdfExport: jsPDF found after ' + attempts + ' polls (~' + (attempts * 100) + 'ms)');
        patchJsPDFProto(jsPDF);
        return;
      }
      if (attempts < 12000) setTimeout(tryHook, 100);
      else console.warn('[CAB MW] hookPdfExport: gave up polling after 20 minutes');
    }
    tryHook();
  }

  function patchJsPDFProto(jsPDF) {
    if (!jsPDF) {
      console.warn('[CAB MW] patchJsPDFProto: jsPDF is falsy, aborting');
      return;
    }

    // jsPDF 2.x uses an "API" pattern: methods live on jsPDF.API
    // and the constructor copies them onto each new instance via
    //     for (var prop in API) this[prop] = API[prop];
    // This means jsPDF.prototype.save is UNDEFINED — save isn't
    // a prototype method, it's an instance property assigned at
    // construction time from jsPDF.API.save. Patching prototype
    // does nothing (which is what Kyle hit through v2.4/v2.5).
    //
    // The correct patch point is jsPDF.API.save. Because the
    // constructor reads API.save at instantiation, replacing
    // API.save BEFORE new jsPDF() means every future instance
    // gets our wrapped version copied onto it.
    if (jsPDF.API && typeof jsPDF.API.save === 'function') {
      if (jsPDF.API._cabMashWaterPatched) {
        console.log('[CAB MW] jsPDF.API.save already patched, skipping');
        return;
      }
      try {
        var origApiSave = jsPDF.API.save;
        jsPDF.API.save = function () {
          console.log('[CAB MW] patched save() called — running mash water injection');
          try { addMashWaterToPdf(this); }
          catch (e) { console.warn('[CAB MW] Mash water PDF inject threw:', e); }
          return origApiSave.apply(this, arguments);
        };
        jsPDF.API._cabMashWaterPatched = true;
        console.log('[CAB MW] jsPDF.API.save successfully wrapped (jsPDF 2.x API pattern)');
        return;
      } catch (err) {
        console.warn('[CAB MW] patchJsPDFProto: API.save wrap failed:', err);
      }
    }

    // Fallback: try jsPDF.prototype.save (in case of future jsPDF
    // versions that move back to prototype methods)
    if (jsPDF.prototype && typeof jsPDF.prototype.save === 'function') {
      if (jsPDF.prototype._cabMashWaterPatched) {
        console.log('[CAB MW] jsPDF.prototype.save already patched, skipping');
        return;
      }
      try {
        var origProtoSave = jsPDF.prototype.save;
        jsPDF.prototype.save = function () {
          console.log('[CAB MW] patched save() called — running mash water injection');
          try { addMashWaterToPdf(this); }
          catch (e) { console.warn('[CAB MW] Mash water PDF inject threw:', e); }
          return origProtoSave.apply(this, arguments);
        };
        jsPDF.prototype._cabMashWaterPatched = true;
        console.log('[CAB MW] jsPDF.prototype.save successfully wrapped (fallback prototype patch)');
        return;
      } catch (err) {
        console.warn('[CAB MW] patchJsPDFProto: prototype.save wrap failed:', err);
      }
    }

    // Neither worked — log diagnostic info so we can figure out
    // what jsPDF version this is and where save actually lives
    console.warn('[CAB MW] patchJsPDFProto: could not find save() to patch. Diagnostic info:', {
      hasAPI: !!jsPDF.API,
      apiSaveType: jsPDF.API ? typeof jsPDF.API.save : 'N/A',
      apiKeys: jsPDF.API ? Object.keys(jsPDF.API).slice(0, 20) : 'N/A',
      hasPrototype: !!jsPDF.prototype,
      prototypeSaveType: jsPDF.prototype ? typeof jsPDF.prototype.save : 'N/A'
    });
  }

  function addMashWaterToPdf(doc) {
    console.log('[CAB MW] addMashWaterToPdf: entered');
    var methodEl = document.getElementById('brewMethod');
    if (!methodEl) {
      console.warn('[CAB MW] addMashWaterToPdf: no #brewMethod element found');
      return;
    }
    if (methodEl.value !== 'allgrain') {
      console.log('[CAB MW] addMashWaterToPdf: brewMethod is "' + methodEl.value + '", not allgrain — skipping');
      return;
    }
    console.log('[CAB MW] addMashWaterToPdf: brewMethod is allgrain, proceeding');

    var mw = window._cabMashWater;
    console.log('[CAB MW] addMashWaterToPdf: window._cabMashWater =', mw);
    var mashTempEl = document.getElementById('mashTemp');
    if (!mashTempEl) {
      console.warn('[CAB MW] addMashWaterToPdf: no #mashTemp element found, aborting');
      return;
    }
    var mashTempLabel = mashTempEl.options[mashTempEl.selectedIndex].text;
    console.log('[CAB MW] addMashWaterToPdf: mashTempLabel =', mashTempLabel);

    var pageW = doc.internal.pageSize.getWidth();
    var pageH = doc.internal.pageSize.getHeight();
    var margin = 25;

    console.log('[CAB MW] addMashWaterToPdf: calling doc.addPage()');
    doc.addPage();
    var y = 20;
    console.log('[CAB MW] addMashWaterToPdf: new page added, drawing content...');

    doc.setFontSize(10.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(245, 158, 11);
    doc.text('MASH WATER PLAN', margin, y + 3.2);
    doc.setDrawColor(245, 158, 11);
    doc.setLineWidth(0.6);
    doc.line(margin, y + 5, pageW - margin, y + 5);
    y += 10;

    if (mw && mw.totalGrain > 0 && typeof doc.autoTable === 'function') {
      var cleanMashTemp = mashTempLabel.split('\u2014')[0].trim();

      doc.autoTable({
        startY: y,
        head: [['Mash Temp', 'Grain Temp', 'Ratio', 'Strike Water', 'Strike Temp', 'Sparge', 'Total Water']],
        body: [[
          cleanMashTemp,
          mw.grainTemp + '\u00B0F',
          mw.ratio.toFixed(2) + ' qt/lb',
          mw.strikeVol.toFixed(1) + ' gal',
          mw.strikeTemp.toFixed(1) + '\u00B0F',
          mw.spargeIsNone ? 'None (Full Vol)' : mw.spargeVol.toFixed(1) + ' gal',
          mw.totalWater.toFixed(1) + ' gal'
        ]],
        margin: { left: margin, right: margin },
        theme: 'plain',
        headStyles: {
          textColor: [26, 26, 26],
          fontSize: 8,
          fontStyle: 'bold',
          cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
          lineColor: [224, 220, 212],
          lineWidth: { bottom: 0.5 }
        },
        bodyStyles: {
          fontSize: 7.5,
          textColor: [26, 26, 26],
          cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
          fillColor: [245, 243, 238]
        }
      });
      y = doc.lastAutoTable.finalY + 6;

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(122, 117, 109);
      doc.text('Target mash character: ' + mashTempLabel, margin, y);
      y += 6;

      // Include the fermentability impact on the PDF too
      if (window.recipe && window.recipe.yeast && typeof window.recipe.yeast.attenuation === 'number') {
        var publishedAtt = window.recipe.yeast.attenuation;
        var effectiveAtt = getEffectiveAttenuation(publishedAtt, mw.mashTemp);
        var delta = mw.mashTemp - MASH_TEMP_BASELINE;
        if (Math.abs(delta) >= 0.5) {
          var direction = delta < 0 ? 'more fermentable (drier, higher ABV)' : 'less fermentable (fuller, lower ABV)';
          doc.setFont('helvetica', 'normal');
          doc.text(
            'Fermentability adjustment: attenuation ' + publishedAtt.toFixed(0) + '% \u2192 ' +
            effectiveAtt.toFixed(1) + '% at ' + mw.mashTemp + '\u00B0F mash \u2014 ' + direction + '.',
            margin, y
          );
          y += 5;
        }
      }

      doc.setFont('helvetica', 'normal');
      doc.text(
        'Single infusion mash. Strike temp assumes 0.2 thermal mass ratio for typical mash tuns.',
        margin, y
      );
    } else {
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(26, 26, 26);
      doc.text('Target mash temp: ' + mashTempLabel, margin, y);
    }

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(122, 117, 109);
    doc.text(
      'Generated by the Craft a Brew Recipe Builder  \u2022  craftabrew.com',
      margin, pageH - 6
    );
  }

  // ============================================================
  // Print export hook — v2.5: stacking guard + idempotent injection
  // --------------------------------------------------------------
  // v2.2/v2.3/v2.4 had a stacking bug: each click to Print installed
  // a fresh wrap over whatever window.open currently was. If the
  // user clicked Print N times without completing a flow, N wraps
  // stacked. When printRecipe finally called window.open, the call
  // unwound through all N wraps and each scheduled its own
  // injection — producing N copies of MASH WATER PLAN in the
  // printed output. Kyle hit 3x on April 14.
  //
  // v2.5 defense in depth:
  //   1. _wrapInstalled flag — second/third clicks do nothing if
  //      a wrap is already active (the existing wrap catches them)
  //   2. injectIntoPrintWindow checks for an existing
  //      <h2>MASH WATER PLAN</h2> in the window and aborts if
  //      found, so even if wraps somehow stack the actual DOM
  //      mutation happens exactly once
  // ============================================================
  var _wrapInstalled = false;

  function hookPrintExport() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest
        ? e.target.closest('.cab-export-btn, .cab-sidebar-export-btn')
        : null;
      if (!btn) return;
      if (btn.getAttribute('data-action') !== 'print') return;

      var methodEl = document.getElementById('brewMethod');
      if (!methodEl || methodEl.value !== 'allgrain') return;

      // Stacking guard: don't install a new wrap if one is already
      // installed. The existing wrap will catch the next window.open.
      if (_wrapInstalled) {
        console.log('[CAB MW] Print wrap already installed, skipping duplicate');
        return;
      }

      _wrapInstalled = true;
      var origOpen = window.open;
      var handled = false;

      window.open = function () {
        var win = origOpen.apply(window, arguments);
        if (!handled) {
          handled = true;
          window.open = origOpen;
          _wrapInstalled = false;
          if (win) setTimeout(function () { injectIntoPrintWindow(win); }, 100);
        }
        return win;
      };

      // Safety: restore after 120s if the user cancels the modal
      // or the flow never reaches window.open.
      setTimeout(function () {
        if (!handled) {
          handled = true;
          window.open = origOpen;
          _wrapInstalled = false;
        }
      }, 120000);
    }, true);
  }

  // Thin wrapper: the print flow passes a window, the save flow
  // passes a document directly. Both delegate to injectIntoDoc.
  function injectIntoPrintWindow(win) {
    if (win && win.document) injectIntoDoc(win.document);
  }

  // ============================================================
  // injectIntoDoc — shared injection for both print and save
  // --------------------------------------------------------------
  // v2.7 extracted from injectIntoPrintWindow so the save-as-pdf
  // flow in recipe-pdf-export.js v3.14 can call it directly on an
  // offscreen iframe document. Exposed as
  // window._cabInjectMashWaterIntoDoc for recipe-pdf-export to use.
  // ============================================================
  function injectIntoDoc(doc) {
    try {
      if (!doc || !doc.body) return;

      // Idempotency: skip if MASH WATER PLAN already present
      var existingH2s = doc.querySelectorAll('h2');
      for (var j = 0; j < existingH2s.length; j++) {
        if ((existingH2s[j].textContent || '').indexOf('MASH WATER PLAN') >= 0) {
          console.log('[CAB MW] MASH WATER PLAN already present, skipping duplicate inject');
          return;
        }
      }

      // Only inject on all-grain recipes
      var methodEl = document.getElementById('brewMethod');
      if (!methodEl || methodEl.value !== 'allgrain') {
        console.log('[CAB MW] injectIntoDoc: not all-grain, skipping');
        return;
      }

      var mw = window._cabMashWater;
      var mashTempEl = document.getElementById('mashTemp');
      if (!mashTempEl) return;
      var mashTempLabel = mashTempEl.options[mashTempEl.selectedIndex].text;
      var cleanMashTemp = mashTempLabel.split('\u2014')[0].trim();

      var html = '<h2>MASH WATER PLAN</h2>';
      if (mw && mw.totalGrain > 0) {
        html +=
          '<table>' +
          '<tr><th>Mash Temp</th><th>Grain Temp</th><th>Ratio</th><th>Strike Water</th><th>Strike Temp</th><th>Sparge</th><th>Total Water</th></tr>' +
          '<tr>' +
            '<td>' + escapeHtml(cleanMashTemp) + '</td>' +
            '<td>' + mw.grainTemp + '\u00B0F</td>' +
            '<td>' + mw.ratio.toFixed(2) + ' qt/lb</td>' +
            '<td>' + mw.strikeVol.toFixed(1) + ' gal</td>' +
            '<td>' + mw.strikeTemp.toFixed(1) + '\u00B0F</td>' +
            '<td>' + escapeHtml(mw.spargeIsNone ? 'None (Full Vol)' : mw.spargeVol.toFixed(1) + ' gal') + '</td>' +
            '<td>' + mw.totalWater.toFixed(1) + ' gal</td>' +
          '</tr>' +
          '</table>' +
          '<div class="yeast-detail-line">Target mash character: ' + escapeHtml(mashTempLabel) + '</div>';

        // Add fermentability note if a yeast is selected and
        // mash temp deviates from baseline
        if (window.recipe && window.recipe.yeast && typeof window.recipe.yeast.attenuation === 'number') {
          var publishedAtt = window.recipe.yeast.attenuation;
          var effectiveAtt = getEffectiveAttenuation(publishedAtt, mw.mashTemp);
          var delta = mw.mashTemp - MASH_TEMP_BASELINE;
          if (Math.abs(delta) >= 0.5) {
            var direction = delta < 0 ? 'more fermentable (drier, higher ABV)' : 'less fermentable (fuller, lower ABV)';
            html +=
              '<div class="yeast-detail-line">Fermentability: attenuation ' +
              publishedAtt.toFixed(0) + '% \u2192 ' + effectiveAtt.toFixed(1) +
              '% at ' + mw.mashTemp + '\u00B0F mash \u2014 ' + direction + '.</div>';
          }
        }
      } else {
        html += '<div class="yeast-detail-line">Target mash temp: ' + escapeHtml(mashTempLabel) + '</div>';
      }

      var container = doc.createElement('div');
      container.innerHTML = html;

      var headings = doc.querySelectorAll('h2');
      var brewNotesH = null;
      for (var i = 0; i < headings.length; i++) {
        if ((headings[i].textContent || '').indexOf('BREW DAY NOTES') >= 0) {
          brewNotesH = headings[i];
          break;
        }
      }
      if (brewNotesH && brewNotesH.parentNode) {
        while (container.firstChild) {
          brewNotesH.parentNode.insertBefore(container.firstChild, brewNotesH);
        }
      } else {
        var pageInner = doc.querySelector('.page-inner') || doc.body;
        while (container.firstChild) pageInner.appendChild(container.firstChild);
      }
      console.log('[CAB MW] injectIntoDoc: MASH WATER PLAN section inserted');
    } catch (e) {
      console.warn('[CAB MW] injectIntoDoc failed:', e);
    }
  }

  // Expose as public API for recipe-pdf-export.js v3.14+ to call
  // during its save-as-pdf flow (offscreen iframe capture path).
  window._cabInjectMashWaterIntoDoc = injectIntoDoc;

  // ============================================================
  // Utilities
  // ============================================================
  function setText(id, txt) {
    var el = document.getElementById(id);
    if (el) el.textContent = txt;
  }
  function getVal(id) {
    var el = document.getElementById(id);
    return el ? el.value : '';
  }
  function setVal(id, v) {
    var el = document.getElementById(id);
    if (el && v != null && v !== '') el.value = v;
  }
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
})();